import { BadgeCardRow } from '@bp/ui-components';
import { WarningType } from '../../utils/getWarningTypeTranslation';
import { HolidayType } from '../Holidays/graphql';

export type GridContext = 'classes' | 'rooms' | 'subjects' | 'teachers';

export type GridHighlightType = 'warnings' | 'rooms' | 'noIcons' | 'lengthDeviation';
export type ResizeEdge = 'start' | 'end';

export type Item = {
  label: string;
  value: string;
};

export type RowItem = Item & { subLabel?: string | null };

export type RoomItem = Item & { group: RoomTypes; inUse?: boolean | null; buildingName?: string | null };

export type RoomTypes = 'assigned' | 'lesson' | 'subject' | 'teacher' | 'class' | 'school';

export type PickedCard = {
  card: TeachingBlockCardType;
  counter?: number;
  position: { x: number; y: number };
  fromStack?: boolean;
};

/**
 * Die Version, wie die Stores sie brauchen: Kopfdaten und die Ressourcen der Version als Zeilen
 * des Boards. Der Context baut sie aus dem Lesemodell (`teachingBlockVersionData`) und den Stammdaten.
 */
export type TeachingBlockBoardVersion = {
  uuid: string;
  version: number;
  description?: string | null;
  active?: boolean | null;
  editable?: boolean | null;
  isSnapshot?: boolean | null;
  classesConnection: {
    edges: Array<{ node: { uuid: string; name: string; shortName: string; grade?: number | null } }>;
  };
  teachersConnection: {
    edges: Array<{ node: { uuid: string; shortName: string; displayNameShort?: string | null } }>;
  };
  roomsConnection: {
    edges: Array<{ node: { uuid: string; name: string; building?: { name?: string | null } | null } }>;
  };
};

export type PlacedTeachingBlockCardType = TeachingBlockCardType & {
  startDate: Date;
  endDate: Date;
};

export type TeachingBlockCardType = {
  uuid: string;
  duration?: number | null;
  startDate?: Date | null;
  endDate?: Date | null;
  locked?: boolean;
  includeHoliday?: boolean;
  durationInDays: number;
  lesson?: {
    uuid: string;
    name?: string;
    // Wahlfach: solche Karten dürfen sich im Board eine Zeile teilen und werden
    // gestapelt dargestellt (siehe isCoexistableTeachingBlockCard).
    elective?: boolean | null;
    roomSupplyConnection: {
      edges: Array<{
        node: {
          uuid: string;
        };
      }>;
    };
  } | null;
  subject?: {
    uuid: string;
    name?: string;
    shortName?: string;
    defaultRooms: Array<{
      uuid: string;
    }>;
    /** Fachfarbe aus den Stundenplan-Einstellungen; liegt über `_SubjectFragment` bereits an. */
    timetableConfig?: { color?: string | null } | null;
  } | null;
  classes: Array<{
    uuid: string;
    name?: string;
    shortName?: string;
    defaultRoom?: {
      uuid: string;
    } | null;
  }>;
  teachers: Array<{
    uuid: string;
    shortName?: string;
    listName?: string;
    displayNameShort?: string | null;
    defaultRoom?: {
      uuid: string;
    } | null;
  }>;
  rooms: Array<{
    __typename?: 'Room';
    uuid: string;
  }>;
  groups: Array<{
    uuid: string;
    name?: string;
  }>;
  lessonClasses: Array<{
    class: {
      uuid: string;
    };
    groups: Array<{
      uuid: string;
    }>;
    usedDivision?: {
      uuid: string;
    } | null;
  }>;
  pinned?: boolean;
  loading?: boolean;
  label?: string;
  labelShort?: string;
  virtualParent?: string;
  virtualChildren?: string[];
  availabilityWarnings?: Array<{
    type: WarningType;
    data: Array<{
      name: string;
      uuid: string;
    }>;
  }>;
  badgeCardRows: BadgeCardRow[];
  badgeCardTextColor: string;
  classGridCards: Array<{
    uuid: string;
    classUuid: string;
    cssHeight?: string | null;
    cssTop?: string | null;
    cssGradientString?: string | null;
    textColor?: string | null;
  }>;
  teacherGridCards: Array<{
    uuid: string;
    teacherUuid: string;
    cssHeight?: string | null;
    cssTop?: string | null;
    cssGradientString?: string | null;
    textColor?: string | null;
  }>;
  roomGridCards: Array<{
    uuid: string;
    roomUuid: string;
    cssHeight?: string | null;
    cssTop?: string | null;
    cssGradientString?: string | null;
    textColor?: string | null;
  }>;
  groupClassNames: string;
  overflow?: number;
  editable?: boolean;
};

/**
 * Karten von Wahlfächern dürfen sich im Board eine Zeile teilen: statt sich gegenseitig
 * zu verdrängen bzw. zu kürzen, werden sie gestapelt und teilen sich die Zeilenhöhe.
 * Pendant zu `isCoexistableCard` im TimetableGrid – dort zählen zusätzlich
 * Funktionsklassen dazu, im TeachingBlockGrid bewusst nur `elective`.
 */
export const isCoexistableTeachingBlockCard = (card: Pick<TeachingBlockCardType, 'lesson'>): boolean =>
  card.lesson?.elective === true;

export type TeachingBlockVirtualCard = {
  uuid: string;
  parentUuid: string;
  startDateString: string;
  endDateString: string;
  duration: number;
};

export type GridDateType = {
  weekNumber: number;
  dateString: string;
  holidayUuid: string | null;
};

export type GridWeekTypeNEW = Map<number, Map<string, GridDateType>>;

export type GridHolidayType = {
  uuid: string;
  name: string;
  start: string;
  end: string;
};

export type GridWeekType = {
  number: number;
  holiday: HolidayType | null;
  start: Date;
  end: Date;
  offsetRight: number;
  offsetLeft: number;
};

export type Warnings = Array<{
  type: WarningType;
  data?: Array<{ name: string; uuid: string; involvedCard?: TeachingBlockCardType }>;
}>;

export type ConflictModalType = {
  isOpen?: boolean;
  isLoading?: boolean;
  conflicts?: Warnings;
  card?: TeachingBlockCardType;
  startDateString?: string;
};

export type TimeConflictModalType = {
  isOpen?: boolean;
  isLoading?: boolean;
  card?: TeachingBlockCardType;
  week?: GridWeekType;
  holidays?: HolidayType[];
  cards?: TeachingBlockCardType[];
  start?: Date;
  end?: Date;
  startDateString?: string;
  /** Nur beim Resize gesetzt: markiert den Resize-Fall (gezogener Bereich) für split/ignore. */
  endDateString?: string;
  holidayUuids?: string[];
};

export type CardDurationModalType = {
  isOpen?: boolean;
  card?: TeachingBlockCardType;
  isVirtual?: boolean;
  previousHoliday?: HolidayType;
  nextHoliday?: HolidayType;
  previousCard?: TeachingBlockCardType;
  nextCard?: TeachingBlockCardType;
};

export type CardRoomsModalType = {
  isOpen?: boolean;
  card?: PlacedTeachingBlockCardType | TeachingBlockCardType | null;
};

export type CardResizeRange = {
  startDateString: string;
  endDateString: string;
};

export type CardAutoFitOptions = {
  canResizeEnd?: boolean;
  canResizeStart?: boolean;
  isVirtual?: boolean;
  range?: CardResizeRange;
};

/**
 * Verzögerung (ms), um die der Einzelklick (Karte aufnehmen) zurückgehalten wird,
 * damit ein Doppelklick (Auto-Fit in die Lücke) ihn rechtzeitig abbrechen kann.
 *
 * Das ist gleichzeitig das Zeitfenster für den Doppelklick: Erst nach Ablauf nimmt
 * der Einzelklick die Karte auf (wodurch sie verschwindet und ein `dblclick` nicht
 * mehr feuern könnte). Höher = entspannterer Doppelklick, aber trägeres Aufnehmen.
 */
export const DOUBLE_CLICK_DELAY_MS = 300;
